import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { paths } from "@/lib/api-types";
import { likeFilter, joinOr } from "./filters";
import { adminQueryKeys } from "./queryKeys";
import {
  createListQueryInit,
  createDetailQueryInit,
  listQueryOptions,
  detailQueryOptions,
} from "./hooks";
import type { SortOrder } from "./sort";

export type AdminGroupListItem =
  paths["/api/admin/groups"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

export type AdminGroupDetail =
  paths["/api/admin/groups/{id}"]["get"]["responses"]["200"]["content"]["application/json"]["group"];

type AdminGroupsListQuery = {
  page: number;
  pageSize: number;
  sortField?: string | null;
  sortOrder: SortOrder;
  filterText?: string;
};

export function useAdminGroupsList(params: AdminGroupsListQuery) {
  const filterText = params.filterText?.trim() ?? "";
  const filter = useMemo(() => {
    if (filterText === "") return undefined;
    // Support filtering by name or type
    return joinOr([
      likeFilter("name", filterText),
      likeFilter("type", filterText),
    ]);
  }, [filterText]);
  const init = createListQueryInit(
    { ...params, filter },
    "name",
  );
  return apiClient.useQuery("get", "/api/admin/groups" as any, init, listQueryOptions);
}

export function useAdminGroup(groupId: string | undefined) {
  const init = createDetailQueryInit(groupId);
  return apiClient.useQuery(
    "get",
    "/api/admin/groups/{id}" as any,
    init,
    detailQueryOptions(!!groupId),
  );
}

export function useAdminGroupCreate() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("post", "/api/admin/groups" as any, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.groups.list,
      });
    },
  });
}

export function useAdminGroupUpdate() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("patch", "/api/admin/groups/{id}" as any, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.groups.list,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.groups.detail,
      });
    },
  });
}

export function useAdminGroupDelete() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("delete", "/api/admin/groups/{id}" as any, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.groups.list,
      });
    },
  });
}

export function useAdminGroupAddMember() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("post", "/api/admin/groups/{id}/members" as any, {
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.groups.list,
      });
      await queryClient.invalidateQueries({
        queryKey: [
          ...adminQueryKeys.groups.detail,
          { params: { path: { id: variables.params.path.id } } },
        ],
      });
    },
  });
}

export function useAdminGroupRemoveMember() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("delete", "/api/admin/groups/{id}/members/{userId}" as any, {
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.groups.list,
      });
      await queryClient.invalidateQueries({
        queryKey: [
          ...adminQueryKeys.groups.detail,
          { params: { path: { id: variables.params.path.id } } },
        ],
      });
    },
  });
}

